import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Menu } from './menu'
import classNames from 'classnames'
import SvgLogo from '../generated/svg/logo'
import SvgMenuBar from '../generated/svg/menu-bar'
import { SvgCross } from './svg'
import { querySelectorWithShadow } from '../utils/query-selector-with-shadow'
import SvgUserIcon from '../generated/svg/user-icon'

const Wrapper = styled.div`
  padding-top: var(--header-height);
`

const Outer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;
  transition: opacity 0.4s, transform 0.4s, color 0.4s;
  pointer-events: none;
  background-color: var(--inverted-bg-color);
  color: var(--inverted-text-color);

  &.hide {
    transform: translateY(-100%);
    opacity: 0;
  }
`

const Inner = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 1.375rem 2.5rem 1.375rem 2.5rem;
  @media (max-width: 60rem){
    padding: 1rem;
  }
`

const Logo = styled.a`
  margin-right: auto;
  display: flex;
  align-items: center;
  pointer-events: initial;
  transition: opacity 0.4s;
  &:hover {
    opacity: 0.5;
  }

  svg {
    height: 2.5625rem;
    width: auto;
    display: block;
    @media (max-width: 60rem){
      height: 1.75rem;
    }
  }
`

const Navigator = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
  pointer-events: initial;
  @media (max-width: 70rem){
    &:first-of-type {
      display: none;
    }
  }
  @media (max-width: 40rem){
    display: none;
  }
  &:first-of-type {
    margin-inline: auto;
  }
`

const MobileNavigator = styled(Navigator)`
  display: none;
  @media (max-width: 40rem){
    display: flex !important;
  }
`

const NavigatorItem = styled.a`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: opacity 0.4s;
  &:hover {
    opacity: 0.5;
  }
  &::after {
    content: '';
    width: 100%;
    height: 1px;
    background-color: white;
    display: block;
    bottom: 0;
    left: 0;
    transform: scaleX(0);
    transform-origin: 100% 100%;
    transition: transform 0.6s;
  }
  &:hover &::after {
    transform: none;
    transform-origin: 0 0;
  }
`

const MobileNavigatorItem = styled(NavigatorItem)`
`

const NavigatorItemIcon = styled.div`
  svg {
    display: block;
    height: 1.75rem;
    width: auto;
    margin-inline: auto;
  }
`

const NavigatorItemLabelEn = styled.div`
  font-size: 0.875rem;
  line-height: 0.9285714285714286;
  letter-spacing: 0.07em;
  font-weight: 400;
  font-family: var(--sans-serif-font);
  @media (max-width: 40rem){
    font-size: 0.75rem;
  }
`

const NavigatorItemLabelJa = styled.div`
  font-size: 0.5625rem;
  line-height: 1.4444444444444444;
  letter-spacing: 0.05em;
  font-weight: 300;
  margin-top: 0.25rem;
  font-weight: 500;
  font-family: var(--sans-serif-font);
  @media (max-width: 40rem){
    font-size: 0.5rem;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: stretch;
  pointer-events: all;
`

const Button = styled.a`
  padding: 0.5rem;
  display: flex;
  align-items: center;
  transition: color 0.4s;
  .open & {
    color: white
  }
  svg {
    display: block;
    height: 1.625rem;
    width: auto;
    @media (max-width: 40rem){
      height: 1.25rem;
      width: auto;
    }
  }
`

const MenuButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 200;
  transition: color 0.4s;
  margin-left: 2rem;
  padding: 0.5rem;
  cursor: pointer;
  @media (max-width: 40rem){
    margin-left: 0.5rem;
  }
  transition: opacity 0.4s;
  &:hover {
    opacity: 0.5;
  }
  &.open {
    color: var(--main-text-color);
  }
  svg {
    display: block;
    height: 1.75rem;
    width: auto;
    aspect-ratio: 1 / 1;
  }
  & ${NavigatorItemLabelJa} {
    display: none;
    @media (max-width: 40rem){
      display: block;
    }
  }
`

type HeaderProps = {

}
export const Header: FunctionComponent<HeaderProps> = () => {
  const [hide, setHide] = useState(false)
  const [open, setOpen] = useState(false)
  const [white, setWhite] = useState(false)
  const outerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    let lastScrollY = window.scrollY
    const listener = () => {
      const scrollY = window.scrollY
      if(scrollY < headerHeight || scrollY < lastScrollY){
        setHide(false)
      } else {
        setHide(true)
      }
      lastScrollY = scrollY
    }
    listener()
    window.addEventListener('scroll', listener)
    return () => {
      window.removeEventListener('scroll', listener)
    }
  }, [headerHeight])

  useEffect(() => {
    const outer = outerRef.current
    if(outer){
      const listener = () => {
        const {height} = outer.getBoundingClientRect()
        document.body.style.setProperty('--header-height', `var(--hide-header-height, ${height}px)`)
        setHeaderHeight(height)
      }
      const observer = new ResizeObserver(listener)
      observer.observe(outer)
      listener()
      return () => {
        observer.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    const topBanner = querySelectorWithShadow('#top-banner')
    if (topBanner) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setWhite(entry.isIntersecting)
          })
        },
        { threshold: [0] }
      )
      observer.observe(topBanner)
      return () => {
        observer.disconnect()
      }
    }
  })
  return <Wrapper>
    <Outer ref={outerRef} className={classNames({hide: !open && hide, open, white})}>
      <Inner>
        <Logo href="/">
          <SvgLogo />
        </Logo>
        <Navigator>
          <NavigatorItem href="/products">
            <NavigatorItemLabelEn>PRODUCTS</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>商品一覧</NavigatorItemLabelJa>
          </NavigatorItem>
          <NavigatorItem href="/concept">
            <NavigatorItemLabelEn>ABOUT US</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>Teaflexについて</NavigatorItemLabelJa>
          </NavigatorItem>
          <NavigatorItem href="/feature">
            <NavigatorItemLabelEn>FEATURE</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>お茶の魅力</NavigatorItemLabelJa>
          </NavigatorItem>
          <NavigatorItem href="/news">
            <NavigatorItemLabelEn>NEWS</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>お知らせ</NavigatorItemLabelJa>
          </NavigatorItem>
          {/* hide column */}
          {/* <NavigatorItem href="/column">
            <NavigatorItemLabelEn>COLUM</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>コラム</NavigatorItemLabelJa>
          </NavigatorItem> */}
          <NavigatorItem href="/faq">
            <NavigatorItemLabelEn>FAQ</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>よくあるご質問</NavigatorItemLabelJa>
          </NavigatorItem>
        </Navigator>
        <Navigator>
          <NavigatorItem href="https://stoked-store.jp/shop/customer">
            <NavigatorItemLabelEn>My Page</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>マイページ</NavigatorItemLabelJa>
          </NavigatorItem>
          {/* <NavigatorItem href="https://stoked-store.jp/shop/cart">
            <NavigatorItemLabelEn>Cart</NavigatorItemLabelEn>
            <NavigatorItemLabelJa>カート</NavigatorItemLabelJa>
          </NavigatorItem> */}
        </Navigator>
        <MobileNavigator>
          <MobileNavigatorItem href="https://stoked-store.jp/shop/customer">
            <NavigatorItemIcon><SvgUserIcon /></NavigatorItemIcon>
            <NavigatorItemLabelJa>マイページ</NavigatorItemLabelJa>
          </MobileNavigatorItem>
        </MobileNavigator>
        <ButtonContainer>
          <MenuButton className={classNames({open})} onClick={() => setOpen(v => !v)}>
            {
              open ? <SvgCross /> : <SvgMenuBar />
            }
            <NavigatorItemLabelJa>メニュー</NavigatorItemLabelJa>
          </MenuButton>
        </ButtonContainer>
        <Menu open={open} />
      </Inner>
    </Outer>
  </Wrapper>
}